import { useMemo } from 'react'
import { FakeLink } from 'components/FbUI/FbLink/styles'
import {
  FilterChipCloseButton,
  FilterChipContainer,
} from 'components/Filters/components/FilterChips/FilterChipStyles'
import { Tooltip } from 'components/Tooltip/tooltip'
import { cn } from 'components/UI/cn'
import {
  EventTypeFilter,
  useEventHistoryFilterStore,
} from './EventHistoryFilterStore'
import useApiService from 'services/api'
import { EventHistoryTab } from './EventHistoryAccordion'

const EVENT_TYPE_LABELS: Record<EventTypeFilter, string> = {
  deal: 'Deals',
  notes: 'Notes',
  reminder: 'Reminders',
  samples: 'Samples',
  email: 'Emails',
  contactcompany: 'Account Attributes',
  document: 'Documents',
}

interface EventHistoryFilterChipsProps {
  selectedTab: EventHistoryTab
}

export function EventHistoryFilterChips({
  selectedTab,
}: EventHistoryFilterChipsProps) {
  const { useGetUsers } = useApiService()
  const { data: users } = useGetUsers()

  const { eventTypes, teamMemberIds, criteria, clearAllFilters } =
    useEventHistoryFilterStore()

  // Only show event type filters when on "all" tab
  const showEventTypeFilters = selectedTab === 'all'
  const hasFilters =
    (showEventTypeFilters && eventTypes.length > 0) || teamMemberIds.length > 0

  const teamMemberNames = useMemo(() => {
    if (!users || teamMemberIds.length === 0) return []
    return teamMemberIds
      .map((id) => {
        const user = users.find((u) => u.id === id)
        if (!user) return null
        return `${user.first_name} ${user.last_name}`.trim() || user.email
      })
      .filter(Boolean) as string[]
  }, [users, teamMemberIds])

  if (!hasFilters) return null

  const handleClearEventTypes = () => {
    if (
      showEventTypeFilters &&
      eventTypes.length > 0 &&
      teamMemberIds.length === 0
    ) {
      // Last filter - clear all
      clearAllFilters()
    } else {
      // Clear just event types
      useEventHistoryFilterStore.getState().setPendingEventTypes([])
      useEventHistoryFilterStore.getState().applyFilters()
    }
  }

  const handleClearTeamMembers = () => {
    if (
      teamMemberIds.length > 0 &&
      (!showEventTypeFilters || eventTypes.length === 0)
    ) {
      // Last filter - clear all
      clearAllFilters()
    } else {
      // Clear just team members
      useEventHistoryFilterStore.getState().setPendingTeamMemberIds([])
      useEventHistoryFilterStore.getState().applyFilters()
    }
  }

  const eventTypeLabels = eventTypes.map((type) => EVENT_TYPE_LABELS[type])
  const eventTypeTooltip = eventTypeLabels.join(', ')
  const isExclusive = criteria === 'neither'

  return (
    <div
      className={cn(
        'd-flex flex-wrap align-items-center justify-content-end gap-2 mt-2',
        'mb-2'
      )}
    >
      {/* Event Type Chips - only show on "all" tab */}
      {showEventTypeFilters && eventTypes.length > 0 && (
        <Tooltip delay={0} content={eventTypeTooltip} placement="top">
          <FilterChipContainer isExclusive={isExclusive}>
            <span>
              Event Type:{' '}
              <b>
                {eventTypes.length === 1
                  ? eventTypeLabels[0]
                  : `${eventTypes.length} selected`}
              </b>
            </span>
            <FilterChipCloseButton
              style={{ color: isExclusive ? '#E44C4C' : '#087443' }}
              onClick={handleClearEventTypes}
            />
          </FilterChipContainer>
        </Tooltip>
      )}

      {/* Team Member Chips */}
      {teamMemberIds.length > 0 && teamMemberNames.length > 0 && (
        <Tooltip delay={0} content={teamMemberNames.join(', ')} placement="top">
          <FilterChipContainer isExclusive={isExclusive}>
            <span>
              Team Member:{' '}
              <b>
                {teamMemberIds.length === 1
                  ? teamMemberNames[0]
                  : `${teamMemberIds.length} selected`}
              </b>
            </span>
            <FilterChipCloseButton
              style={{ color: isExclusive ? '#E44C4C' : '#087443' }}
              onClick={handleClearTeamMembers}
            />
          </FilterChipContainer>
        </Tooltip>
      )}

      {/* Clear All Link */}
      {hasFilters && (
        <FakeLink className={'ml-2'} onClick={clearAllFilters}>
          Clear all
        </FakeLink>
      )}
    </div>
  )
}
